import { useTranslation } from 'react-i18next';

import type { SmoltReportDetail } from '@/domain/smoltReport';
import SectionHeader from '@/routes/Client/Cage/pages/Reports/shared/SectionHeader';

import SmoltReportDivider from './components/SmoltReportDivider';
import SmoltReportFooter from './components/SmoltReportFooter';
import SmoltReportPdfNote from './components/SmoltReportPdfNote';
import SmoltReportWarningsList from './components/SmoltReportWarningsList';
import WeightAndGrowthSection from './components/WeightAndGrowthSection';
import WelfareSection from './components/WelfareSection';
import SmoltReportHeader from './SmoltReportHeader';

interface SmoltReportDetailViewProps {
    report: SmoltReportDetail;
    isLoading?: boolean;
    isPdfExport?: boolean;
    /** A fish group is selected: a report computed for the whole tank says so. */
    isFishGroupSelected?: boolean;
}

export default function SmoltReportDetailView(props: SmoltReportDetailViewProps) {
    const { report, isLoading = false, isPdfExport = false, isFishGroupSelected = false } = props;
    const { t } = useTranslation();

    const hasWarnings =
        report.warnings.weightLowFrequency ||
        report.warnings.weightDataLatency ||
        report.warnings.dataEndsBeforeDelivery ||
        report.warnings.weightPeriodTooShort ||
        report.warnings.welfareLowCoverage ||
        report.warnings.welfareInsufficientSample;

    return (
        <>
            <SmoltReportHeader
                createdAt={report.createdAt}
                deliveryDate={report.deliveryDate}
                isLoading={isLoading}
                lastFeedingDate={report.lastFeedingDate}
                stockingDate={report.stockingDate}
                isCageLevel={isFishGroupSelected && report.fishGroupId == null}
            />
            <SmoltReportDivider />
            {isPdfExport ? (
                <SmoltReportPdfNote hasWarnings={hasWarnings} />
            ) : (
                <SmoltReportWarningsList
                    warnings={report.warnings}
                    deliveryDate={report.deliveryDate}
                    lastMeasurementDay={report.weightData.lastMeasurementDay}
                    welfareWindowEnd={report.welfareData.windowEnd}
                />
            )}
            <WeightAndGrowthSection
                deliveryDate={report.deliveryDate}
                lastFeedingDate={report.lastFeedingDate}
                weightData={report.weightData}
                isLoading={isLoading}
                isPdfExport={isPdfExport}
            />
            <WelfareSection
                isLoading={isLoading}
                welfareData={report.welfareData}
                isPdfExport={isPdfExport}
                unavailableNotice={
                    report.fishGroupId != null && !report.hasWelfareData
                        ? t('smoltReport.detailView.welfareUnavailableForFishGroup')
                        : undefined
                }
            />
            {isPdfExport && hasWarnings && (
                <>
                    <SmoltReportDivider />
                    <SectionHeader title={t('smoltReport.detailView.warningsAppendixTitle')} />
                    <SmoltReportWarningsList
                        warnings={report.warnings}
                        deliveryDate={report.deliveryDate}
                        lastMeasurementDay={report.weightData.lastMeasurementDay}
                        welfareWindowEnd={report.welfareData.windowEnd}
                    />
                </>
            )}
            <SmoltReportDivider marginTop={0} />
            <SmoltReportFooter createdAt={report.createdAt} isLoading={isLoading} />
        </>
    );
}
